import { useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Button, Flex, Spin } from "antd"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import AdjustBookingPriceModal from "ui/Booking/Details/Body/PaymentSummary/Modals/AdjustBookingPrice"
import CancelBookingModal from "../../../BookingActions/CancelBookingModal"
import { deriveCheckInState } from "../../../CheckIn/checkInUtils"
import ExperienceCard from "../ExperienceCard/ExperienceCard"
import { useExperienceExtras } from "../../useExperienceExtras"
import styles from "../../index.module.css"

// The Cancel Booking modal's shape: `id` (numeric booking id) loads the refund breakdown, `uuid`
// submits the cancel; the rest is display.
const toCancelBooking = (reservation) => ({
  id: reservation.bookingId,
  uuid: reservation.uuid,
  title: reservation.experience?.title,
  image: reservation.experience?.imageUrl,
  booking_id: reservation.reservationId,
  booked_on: reservation.created,
  client_name: reservation.client?.name,
  client_email: reservation.client?.email
})

// One experience of an order: its accordion with its own price, waivers and answers, and the two
// actions that act on this booking alone, Adjust Price and Cancel. Payments, Collect
// Payment and every other action belong to the whole reservation. `onChange` reloads it once an
// action went through.
const OrderExperience = ({ entry, onChange }) => {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const [isOpen, setIsOpen] = useState(false)
  const [openModal, setOpenModal] = useState(null)
  const { waivers, answers, isLoading: isExtrasLoading, isFailed: isExtrasFailed, reload: reloadExtras } = useExperienceExtras(
    entry.uuid,
    isOpen && Boolean(entry.data)
  )

  // Still loading, or failed: a placeholder card with what the order already says about it.
  if (!entry.data) {
    return (
      <Flex align="center" justify="space-between" gap={12} className={styles.card}>
        <Flex vertical gap={4}>
          <span className={styles.expTitle}>{entry.booking?.title}</span>
          <span className={styles.expCode}>{entry.booking?.bookingNumber}</span>
        </Flex>
        {entry.error ? (
          <Flex align="center" gap={8}>
            <span className={styles.metaText}>{t("booking.reservation_detail.experience_load_failed")}</span>
            <Button size="small" shape="round" onClick={() => onChange(entry.uuid)}>
              {t("booking.reservation_detail.retry")}
            </Button>
          </Flex>
        ) : (
          <Spin size="small" />
        )}
      </Flex>
    )
  }

  const reservation = entry.data
  const isRequest = reservation.rawStatus === "requested"
  const isCompleted = reservation.rawStatus === "completed"
  const isCanceled = reservation.rawStatus === "canceled"
  const canEditBookings = hasPermission(MODULES.BOOKINGS, ACTIONS.EDIT)
  // The same rules as a single reservation's Cancel and Adjust Booking Price, applied to this booking.
  const canCancel = reservation.canEdit && !isRequest && !isCompleted && !isCanceled
  const canAdjustPrice = canEditBookings && !isCanceled && Boolean(reservation.payments)
  const isMarketplaceBooking = reservation.bookingSource === "marketplace" || reservation.bookingSource === "website"

  const closeAndRefresh = () => {
    setOpenModal(null)
    onChange(entry.uuid)
  }

  const checkedInCount = reservation.checkedInCount ?? 0
  const totalGuests = reservation.totalGuests ?? 0

  return (
    <>
      <ExperienceCard
        experience={reservation.experience}
        waivers={waivers}
        waiversLoading={isExtrasLoading}
        answers={answers}
        answersLoading={isExtrasLoading}
        addOns={reservation.addOns}
        bookingUuid={reservation.uuid}
        onRefresh={reloadExtras}
        checkInState={reservation.checkInState || deriveCheckInState(checkedInCount, totalGuests, reservation.checkedOutCount ?? 0)}
        checkedInCount={checkedInCount}
        checkedOutCount={reservation.checkedOutCount ?? 0}
        totalGuests={totalGuests}
        // Shown directly on the experience, not behind a menu.
        onAdjustPrice={canAdjustPrice ? () => setOpenModal("adjust_price") : undefined}
        onCancel={canCancel ? () => setOpenModal("cancel") : undefined}
        isStatusShown
        onExpandChange={setIsOpen}
      >
        {isExtrasFailed && (
          <Flex align="center" gap={8} className="mt-s">
            <span className={styles.metaText}>{t("booking.reservation_detail.experience_load_failed")}</span>
            <Button size="small" shape="round" onClick={reloadExtras}>
              {t("booking.reservation_detail.retry")}
            </Button>
          </Flex>
        )}
      </ExperienceCard>

      {openModal === "adjust_price" && (
        <AdjustBookingPriceModal
          onOpen
          onClose={closeAndRefresh}
          bookingUUID={reservation.uuid}
          tripUUID={reservation.tripUUID}
          currentPrice={reservation.payments.total}
          paidAmount={reservation.payments.paidAmount}
          isMarketplaceBooking={isMarketplaceBooking}
          paymentOption={reservation.paymentOption}
        />
      )}
      {openModal === "cancel" && (
        <CancelBookingModal open onCancel={() => setOpenModal(null)} onConfirm={closeAndRefresh} bookings={[toCancelBooking(reservation)]} />
      )}
    </>
  )
}

OrderExperience.propTypes = {
  // useOrderExperiences' entry: the mapped reservation once loaded (`data`), else the order's
  // summary of it (`booking`) while loading or after `error`.
  entry: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    data: PropTypes.object,
    booking: PropTypes.shape({ title: PropTypes.string, bookingNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
    isLoading: PropTypes.bool,
    error: PropTypes.object
  }).isRequired,
  onChange: PropTypes.func.isRequired
}

export default OrderExperience
